import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  ArrowDownRight,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDeposits() {
  const navigate = useNavigate();
  const [deposits, setDeposits] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [depositsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/deposits`),
        axios.get(`${API_URL}/admin/users`)
      ]);
      setDeposits(depositsRes.data);
      
      const usersMap = {};
      usersRes.data.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsers(usersMap);
    } catch (error) {
      // Error silenced: 'Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Emit event to notify AdminLayout to refresh counts
  const notifyCountsChanged = () => {
    window.dispatchEvent(new CustomEvent('pendingCountsChanged'));
  };

  const handleApprove = async (depositId) => {
    try {
      await axios.put(`${API_URL}/admin/deposits/${depositId}/approve`);
      toast.success('Depósito aprovado com sucesso');
      fetchData();
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar depósito');
    }
  };

  const handleReject = async (depositId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar este depósito?')) return;
    
    try {
      await axios.put(`${API_URL}/admin/deposits/${depositId}/reject`);
      toast.success('Depósito rejeitado');
      fetchData();
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar depósito');
    }
  };

  const handleDelete = async (depositId, depositAmount, userName) => {
    if (!window.confirm(`⚠️ ATENÇÃO: Tem certeza que deseja ELIMINAR PERMANENTEMENTE este depósito de ${formatCurrency(depositAmount)} de ${userName}?\n\nEsta ação não pode ser desfeita e irá:\n- Remover o depósito do sistema\n- Reverter o saldo se já foi aprovado`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/deposits/${depositId}`);
      toast.success('Depósito eliminado permanentemente');
      fetchData();
      notifyCountsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar depósito');
    }
  };

  const handleForceConfirm = async (depositId) => {
    if (!window.confirm('Confirmar que o pagamento foi recebido? O depósito passará para estado "Pendente" para aprovação.')) return;
    
    try {
      await axios.post(`${API_URL}/admin/deposits/${depositId}/force-confirm`);
      toast.success('Pagamento confirmado! Depósito pendente de aprovação.');
      fetchData();
      notifyCountsChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="badge-pending flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        );
      case 'pending_payment':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Aguardando Pagamento
          </span>
        );
      case 'approved':
        return (
          <span className="badge-approved flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Aprovado
          </span>
        );
      case 'rejected':
        return (
          <span className="badge-rejected flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejeitado
          </span>
        );
      default:
        return null;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'mbway':
        return { label: 'MB Way', color: 'text-pink-400', bg: 'bg-pink-500/10' };
      case 'card':
        return { label: 'Cartão', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
      case 'transferencia':
      default:
        return { label: 'Transferência', color: 'text-blue-400', bg: 'bg-blue-500/10' };
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('pt-PT', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  return (
    <div className="animate-fade-in" data-testid="admin-deposits-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Gestão de Depósitos</h1>
          <p className="text-slate-400 text-sm">{deposits.filter(d => d.status === 'pending').length} pendentes</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : deposits.length > 0 ? (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-white/5">
                  <th>Cliente</th>
                  <th>Montante</th>
                  <th>Método</th>
                  <th>Data</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="text-white">{users[deposit.user_id]?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="text-white font-semibold">{formatCurrency(deposit.amount)}</td>
                    <td>
                      {(() => {
                        const methodInfo = getPaymentMethodLabel(deposit.payment_method);
                        return (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${methodInfo.bg} ${methodInfo.color}`}>
                            {methodInfo.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="text-slate-400">
                      {(() => {
                        const dt = formatDateTime(deposit.created_at);
                        return (
                          <div className="flex flex-col">
                            <span>{dt.date}</span>
                            <span className="text-xs text-slate-500">{dt.time}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td>{getStatusBadge(deposit.status)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {deposit.status === 'pending_payment' && (
                          <Button
                            onClick={() => handleForceConfirm(deposit.id)}
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 rounded-lg h-8"
                            data-testid={`confirm-payment-${deposit.id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Confirmar
                          </Button>
                        )}
                        {deposit.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleApprove(deposit.id)}
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 rounded-lg h-8"
                              data-testid={`approve-deposit-${deposit.id}`}
                            >
                              Aprovar
                            </Button>
                            <Button
                              onClick={() => handleReject(deposit.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg h-8"
                              data-testid={`reject-deposit-${deposit.id}`}
                            >
                              Rejeitar
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => handleDelete(deposit.id, deposit.amount, users[deposit.user_id]?.name || 'N/A')}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10 rounded-lg h-8 px-2"
                          title="Eliminar permanentemente"
                          data-testid={`delete-deposit-${deposit.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <ArrowDownRight className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Sem depósitos</h3>
          <p className="text-slate-400">Não há depósitos registados.</p>
        </div>
      )}
    </div>
  );
}
